<template>
  <div class="alt-markets coupon" v-if="markets && markets.length">
    <div v-for="(altMarketGroup, altMarketGroupName) in groupAltMarkets" :key="altMarketGroupName">
      <!-- Group header -->
      <h4 class="event-market-header date">
        <span
          class="market-name"
          :class="{ 'expanded-market': expandedAltMarketGroups[altMarketGroupName] }"
          @click="toggleAltMarketGroupExpansion(altMarketGroupName)"
        >
          <span class="market-name-title">{{ altMarketGroupName }}</span>
          <span class="flex icon-span">
            <sgp-tag :markets="getSGMProcessedMarkets(altMarketGroup.markets)" :isLive="isLiveEventPage"></sgp-tag>
            <span v-if="isBBenabled && isMarketEligibleForBB(altMarketGroup.markets)" class="available-for-bb"></span>
            <v-icon v-if="!expandedAltMarketGroups[altMarketGroupName]">expand_more</v-icon>
            <v-icon v-else>expand_less</v-icon>
          </span>
        </span>
      </h4>

      <!-- Header with participant names (if first market has A/H selections) -->
      <div
        v-if="expandedAltMarketGroups[altMarketGroupName] && (
              altMarketGroup.markets[0].selections.filter(sel => sel.hadvalue === 'A').length > 0 ||
              altMarketGroup.markets[0].selections.filter(sel => sel.hadvalue === 'H').length > 0
            )"
        class="alt-handicaps-header"
      >
        <span class="alt-handicaps-header-name">{{ event.participantname_home }}</span>
        <span class="alt-handicaps-header-name">{{ event.participantname_away }}</span>
      </div>

      <!-- Render alt markets inside group -->
      <div v-if="expandedAltMarketGroups[altMarketGroupName]">
        <div v-for="market in getVisibleAltMarkets(altMarketGroup.markets, altMarketGroupName)" :key="market.id">
          <markets
            :markets="[market]"
            :isAlternativeMarket="true"
            :event="event"
            :diplayOnPage="'live-single-event'"
            :isLiveEventPage="isLiveEventPage"
            :pricechangehighlight="isLiveEventPage"
          />
        </div>
      </div>

      <!-- "See More / See Less" button -->
      <div v-if="altMarketSelectionsCountByGroup[altMarketGroupName] > 6 && expandedAltMarketGroups[altMarketGroupName]">
        <div class="show-more-btn">
          <v-btn :ripple="false" flat @click.stop="toggleAltMarketSelectionsVisibility(altMarketGroupName)">
            <span v-show="!expandedAltMarketSelections[altMarketGroupName]">{{ $t('BettingOffer.SeeMore') }}</span>
            <span v-show="expandedAltMarketSelections[altMarketGroupName]">{{ $t('BettingOffer.SeeLess') }}</span>
          </v-btn>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import markets from '@/components/sports/bettingoffer/markets'
import BettingOffer from '@/components/mixins/BettingOffer'
import sgpTag from '@/components/sports/betslipV2/common/marketSgpTag.vue'
import killswitch from '@/components/mixins/killswitch'
import config from '@/config'

export default {
  name: 'AltMarkets',

  components: { markets, sgpTag },

  mixins: [BettingOffer, killswitch],

  props: [
    'markets',
    'event',
    'diplayOnPage',
    'isLiveEventPage',
    'pricechangehighlight',
    'expandFirst'
  ],

  data () {
    return {
      expandedAltMarketGroups: {},
      expandedAltMarketSelections: {}
    }
  },

  computed: {
    groupAltMarkets () {
      const grouped = {}
      if (!Array.isArray(this.markets)) return grouped

      this.markets.forEach(market => {
        if (!market || !market.name) return
        const key = market.name.trim()
        if (!grouped[key]) grouped[key] = { markets: [] }
        grouped[key].markets.push(market)
      })

      Object.values(grouped).forEach(group => {
        group.markets.sort((a, b) => {
          const ha = a.currentmatchhandicap != null ? Number(a.currentmatchhandicap) : -Infinity
          const hb = b.currentmatchhandicap != null ? Number(b.currentmatchhandicap) : -Infinity
          return hb - ha
        })
      })

      return grouped
    },

    isBBenabled () {
      return config.app.autoconf.IS_BUYBACK_ENABLED
    },

    altMarketSelectionsCountByGroup () {
      const counts = {}
      Object.keys(this.groupAltMarkets).forEach(groupName => {
        let total = 0
        this.groupAltMarkets[groupName].markets.forEach(market => {
          if (Array.isArray(market.selections)) total += market.selections.length
        })
        counts[groupName] = total
      })
      return counts
    }
  },

  methods: {
    toggleAltMarketGroupExpansion (groupName) {
      Vue.set(this.expandedAltMarketGroups, groupName, !this.expandedAltMarketGroups[groupName])
    },

    toggleAltMarketSelectionsVisibility (groupName) {
      Vue.set(this.expandedAltMarketSelections, groupName, !this.expandedAltMarketSelections[groupName])
    },

    getVisibleAltMarkets (altMarkets, groupName) {
      if (!Array.isArray(altMarkets)) return altMarkets
      if (this.expandedAltMarketSelections[groupName]) return altMarkets
      return altMarkets.slice(0, 3)
    },

    getSGMProcessedMarkets (markets) {
      if (!Array.isArray(markets)) return []
      const processed = this.ksPreProcessMarkets(markets, this.event)
      return processed.filter(market => market.csvpricetypes && market.csvpricetypes.includes('SGM')) || []
    },

    isMarketEligibleForBB (markets) {
      if (!Array.isArray(markets)) return false
      return markets.some(market => market.BB === true)
    }
  },

  watch: {
    groupAltMarkets: {
      immediate: true,
      handler (newGroups) {
        const expandedGroups = { ...this.expandedAltMarketGroups }
        const expandedSelections = { ...this.expandedAltMarketSelections }

        Object.keys(newGroups).forEach((groupName, index) => {
          if (!(groupName in expandedGroups)) {
            expandedGroups[groupName] = this.expandFirst && index === 0
          }
          if (!(groupName in expandedSelections)) {
            expandedSelections[groupName] = false
          }
        })

        this.expandedAltMarketGroups = expandedGroups
        this.expandedAltMarketSelections = expandedSelections
      }
    }
  }
}
</script>

<style lang="stylus" scoped>
.show-more-btn
  display: flex
  justify-content: center

.alt-handicaps-header
  display: flex
  justify-content: space-between
  font-size: 12px
  padding: 10px 15px
  margin-bottom: 10px
  border-bottom: 1px solid #e0e0e0

.alt-handicaps-header-name
  display: flex
  width: 50%
  justify-content: center
  font-size: 14px
  font-family: "Roboto Condensed"
</style>



// WEBPACK FOOTER //
// src/components/sports/bettingoffer/AltMarkets.vue